import { DayGroup, DayTimeDeduction, DayNumber } from '@/types';
import { DayNumber as DayNumberEnum } from '@/types/enums';

/**
 * CENTRALIZED DAY MAPPING FUNCTION
 * Converts JavaScript Date.getDay() to your shift data format
 * JavaScript: Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
 * Your Format: Sunday=1, Monday=2, Tuesday=3, Wednesday=4, Thursday=5, Friday=6, Saturday=7
 * 
 * @param date - Date object or date string
 * @returns Day number in your format (1=Sunday, 2=Monday, ..., 7=Saturday)
 */
export function getShiftDayNumber(date: Date | string): number {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const jsDay = dateObj.getDay(); // 0=Sunday, 1=Monday, etc.
  
  // Convert to your format: 1=Sunday, 2=Monday, etc.
  return jsDay === 0 ? DayNumberEnum.SUNDAY : jsDay + 1;
}

/**
 * Converts your shift day number to JavaScript day number
 * @param shiftDay - Day number in your format (1=Sunday, 2=Monday, etc.)
 * @returns JavaScript day number (0=Sunday, 1=Monday, etc.)
 */
export function getJsDayNumber(shiftDay: number): number {
  return shiftDay === DayNumberEnum.SUNDAY ? 0 : shiftDay - 1;
}

/**
 * Convert break hours to decimal format
 */
export function toBreakHrsDecimal(breakHours: { hours: number; minutes: number }): number {
  return Number((breakHours.hours + breakHours.minutes / 60).toFixed(2));
}

/**
 * Convert decimal break hours to hours/minutes format
 */
export function fromBreakHrsDecimal(breakHrs: number): { hours: number; minutes: number } {
  const hours = Math.floor(breakHrs);
  const minutes = Math.round((breakHrs % 1) * 60);
  return { hours, minutes };
}

/**
 * Build compact day_time_deduction array from day groups
 * Validates uniqueness and formats data for DB
 */
export function buildDayTimeDeduction(groups: DayGroup[]): DayTimeDeduction[] {
  const used = new Set<number>();
  
  return groups.map(group => {
    const unique = Array.from(new Set(group.days));
    
    if (unique.length === 0) {
      throw new Error('A group must have at least one day');
    }

    // Cross-group duplicate check
    unique.forEach(day => {
      if (used.has(day)) {
        throw new Error(`Day ${day} selected multiple times`);
      }
      used.add(day);
    });

    const break_hrs = toBreakHrsDecimal(group.breakHours);
    
    return {
      day: unique.length === 1 ? unique[0] : unique, // number or array
      start_time: group.start,
      end_time: group.end,
      break_hrs
    };
  });
}

/**
 * Convert day_time_deduction array to day groups for form editing
 */
export function dayTimeDeductionToDayGroups(dayTimeDeduction: DayTimeDeduction[]): DayGroup[] {
  return dayTimeDeduction.map(item => ({
    days: Array.isArray(item.day) ? item.day : [item.day],
    start: item.start_time,
    end: item.end_time,
    breakHours: fromBreakHrsDecimal(item.break_hrs)
  }));
}

/**
 * Get default day group (Mon-Fri)
 */
export function getDefaultDayGroup(): DayGroup {
  return {
    days: [DayNumberEnum.MONDAY, DayNumberEnum.TUESDAY, DayNumberEnum.WEDNESDAY, DayNumberEnum.THURSDAY, DayNumberEnum.FRIDAY], // Mon-Fri (1-based indexing)
    start: '',
    end: '',
    breakHours: { hours: 0, minutes: 0 }
  };
}

/**
 * Get available days for a new group (excluding already used days)
 */
export function getAvailableDays(existingGroups: DayGroup[]): DayNumber[] {
  const usedDays = new Set<number>();
  
  existingGroups.forEach(group => {
    group.days.forEach(day => usedDays.add(day));
  });
  
  const allDays: DayNumber[] = [DayNumberEnum.SUNDAY, DayNumberEnum.MONDAY, DayNumberEnum.TUESDAY, DayNumberEnum.WEDNESDAY, DayNumberEnum.THURSDAY, DayNumberEnum.FRIDAY, DayNumberEnum.SATURDAY]; // Sun-Sat
  return allDays.filter(day => !usedDays.has(day));
}

/**
 * Validate day groups for conflicts and completeness
 */
export function validateDayGroups(groups: DayGroup[]): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (groups.length === 0) {
    errors.push('At least one day group is required');
    return { isValid: false, errors };
  }

  const usedDays = new Set<number>();
  
  groups.forEach((group, index) => {
    // Check if group has days
    if (group.days.length === 0) {
      errors.push(`Day group ${index + 1} must have at least one day selected`);
    }

    // Check for duplicate days
    group.days.forEach(day => {
      if (usedDays.has(day)) {
        errors.push(`Day ${getDayName(day)} is selected in multiple groups`);
      }
      usedDays.add(day);
    });

    // Check start/end times
    if (!group.start) {
      errors.push(`Day group ${index + 1} must have a start time`);
    }
    if (!group.end) {
      errors.push(`Day group ${index + 1} must have an end time`);
    }
    
    // Check time order - allow night shifts (start time can be after end time for overnight shifts)
    // Only validate that start and end times are different
    if (group.start && group.end && group.start === group.end) {
      errors.push(`Day group ${index + 1} start time and end time cannot be the same`);
    }

    // Check break hours
    if (group.breakHours.hours < 0 || group.breakHours.minutes < 0) {
      errors.push(`Day group ${index + 1} break time cannot be negative`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get day name from day number
 */
export function getDayName(day: DayNumber): string {
  const dayNames = {
    [DayNumberEnum.SUNDAY]: 'Sunday',
    [DayNumberEnum.MONDAY]: 'Monday', 
    [DayNumberEnum.TUESDAY]: 'Tuesday',
    [DayNumberEnum.WEDNESDAY]: 'Wednesday',
    [DayNumberEnum.THURSDAY]: 'Thursday',
    [DayNumberEnum.FRIDAY]: 'Friday',
    [DayNumberEnum.SATURDAY]: 'Saturday'
  };
  return dayNames[day];
}

/**
 * Get short day name from day number
 */
export function getShortDayName(day: DayNumber): string {
  const dayNames = {
    [DayNumberEnum.SUNDAY]: 'Sun',
    [DayNumberEnum.MONDAY]: 'Mon',
    [DayNumberEnum.TUESDAY]: 'Tue', 
    [DayNumberEnum.WEDNESDAY]: 'Wed',
    [DayNumberEnum.THURSDAY]: 'Thu',
    [DayNumberEnum.FRIDAY]: 'Fri',
    [DayNumberEnum.SATURDAY]: 'Sat'
  };
  return dayNames[day];
}

/**
 * Format day groups for display
 */
export function formatDayGroupsForDisplay(dayTimeDeduction: DayTimeDeduction[]): string[] {
  return dayTimeDeduction.map(item => {
    const days = Array.isArray(item.day) ? item.day : [item.day];
    const dayNames = days.map(getShortDayName);
    const dayRange = dayNames.length > 2 && isConsecutive(days) 
      ? `${dayNames[0]}–${dayNames[dayNames.length - 1]}`
      : dayNames.join(', ');
      
    const breakDisplay = item.break_hrs > 0 
      ? ` (${formatBreakTime(item.break_hrs)} break)`
      : '';
      
    return `${dayRange} ${item.start_time}–${item.end_time}${breakDisplay}`;
  });
}

/**
 * Check if days are consecutive
 */
function isConsecutive(days: DayNumber[]): boolean {
  if (days.length <= 1) return false;
  const sorted = [...days].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] !== 1) return false;
  }
  return true;
}

/**
 * Format break time for display
 */
function formatBreakTime(breakHrs: number): string {
  const hours = Math.floor(breakHrs);
  const minutes = Math.round((breakHrs % 1) * 60);
  
  if (hours === 0) {
    return `${minutes}min`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}min`;
  }
}

/**
 * Get shift details for a specific day
 * @param dayTimeDeduction - The day_time_deduction array from shift
 * @param dayOfWeek - Day of week (1=Sun, 2=Mon, ..., 7=Sat) or JS Date object
 * @returns Shift details for that day or null if not found
 */
export function getShiftDetailsForDay(
  dayTimeDeduction: DayTimeDeduction[] | null | undefined,
  dayOfWeek: number | Date
): { start_time: string; end_time: string; break_hrs: number } | null {
  if (!dayTimeDeduction || dayTimeDeduction.length === 0) {
    return null;
  }

  // Convert Date to day number if needed using centralized function
  let targetDay: number;
  if (dayOfWeek instanceof Date) {
    targetDay = getShiftDayNumber(dayOfWeek);
  } else {
    targetDay = dayOfWeek;
  }

  // Find the day/time block that includes this day
  for (const block of dayTimeDeduction) {
    const days = Array.isArray(block.day) ? block.day : [block.day];
    if (days.includes(targetDay as DayNumber)) {
      return {
        start_time: block.start_time,
        end_time: block.end_time,
        break_hrs: block.break_hrs
      };
    }
  }

  return null;
}

/**
 * Get current shift details (for today)
 */
export function getCurrentShiftDetails(dayTimeDeduction: DayTimeDeduction[]) {
  return getShiftDetailsForDay(dayTimeDeduction, new Date());
}

/**
 * Check if a shift has working hours for a specific date
 * @param dayTimeDeduction - The day_time_deduction array from shift
 * @param date - Date string in YYYY-MM-DD format or Date object
 * @returns true if the shift has working hours for that date, false otherwise
 */
export function hasShiftWorkingHoursForDate(
  dayTimeDeduction: DayTimeDeduction[] | null | undefined, 
  date: string | Date
): boolean {
  // Early return if dayTimeDeduction is null, undefined, or not an array
  if (!dayTimeDeduction || !Array.isArray(dayTimeDeduction) || dayTimeDeduction.length === 0) {
    return false;
  }

  // Convert date to day number using centralized function
  const targetDay = getShiftDayNumber(date);

  // Check if any day/time block includes this day
  return dayTimeDeduction.some(block => {
    // Additional safety check for each block
    if (!block || typeof block !== 'object') return false;
    
    const days = Array.isArray(block.day) ? block.day : [block.day];
    return days.includes(targetDay as DayNumber);
  });
}

/**
 * NEW: Centralized shift duration calculation
 * @param startTime - Start time in HH:MM format
 * @param endTime - End time in HH:MM format
 * @returns Duration in hours as a formatted string
 */
export function calculateShiftDuration(startTime: string, endTime: string): string {
  if (!startTime || !endTime) return '--';
  
  try {
    const start = new Date(`1970-01-01T${startTime}`);
    const end = new Date(`1970-01-01T${endTime}`);
    
    // Handle overnight shifts
    if (end < start) {
      end.setDate(end.getDate() + 1);
    }
    
    const diffMs = end.getTime() - start.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    
    return `${diffHours.toFixed(1)} hours`;
  } catch {
    return '--';
  }
}

/**
 * NEW: Centralized time formatting
 * @param time - Time in HH:MM:SS or HH:MM format
 * @returns Formatted time in HH:MM format
 */
export function formatShiftTime(time: string): string {
  if (!time) return '--:--';
  // Handle both HH:MM:SS and HH:MM formats
  const timeParts = time.split(':');
  return `${timeParts[0]}:${timeParts[1]}`;
}

/**
 * NEW: Centralized break hours formatting
 * @param breakHrs - Break hours as number
 * @returns Formatted break hours string
 */
export function formatBreakHours(breakHrs: number | null): string {
  if (breakHrs === null || breakHrs === undefined) return 'Not specified';
  if (breakHrs === 0) return 'No break';
  return `${breakHrs} hour${breakHrs === 1 ? '' : 's'}`;
}

/**
 * NEW: Validate shift for specific date
 * @param shift - Shift object
 * @param otDate - OT date in YYYY-MM-DD format
 * @returns Validation result with error details
 */
export function validateShiftForDate(
  shift: { day_time_deduction: DayTimeDeduction[] }, 
  otDate: string
): { isValid: boolean; error?: string; shiftDetails?: { start_time: string; end_time: string; break_hrs: number } } {
  if (!shift?.day_time_deduction || !otDate) {
    return { isValid: false, error: 'Invalid shift data or date' };
  }

  const targetDay = getShiftDayNumber(otDate);
  
  // Check if the shift supports this day
  const shiftForDate = shift.day_time_deduction.find((dayGroup) => {
    if (!dayGroup || typeof dayGroup !== 'object') return false;
    if (Array.isArray(dayGroup.day)) {
      return dayGroup.day.includes(targetDay as DayNumber);
    }
    return dayGroup.day === targetDay;
  });

  if (!shiftForDate) {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const jsDay = getJsDayNumber(targetDay);
    const dayName = dayNames[jsDay];
    return { 
      isValid: false, 
      error: `No shift working hours are there on ${dayName}` 
    };
  }

  return { 
    isValid: true, 
    shiftDetails: {
      start_time: shiftForDate.start_time,
      end_time: shiftForDate.end_time,
      break_hrs: shiftForDate.break_hrs
    }
  };
}
